import {
    ChartSettingsT,
    ColorMapping,
    CustomPalette,
} from 'business/definitions';
import { LinearGradientObject } from 'echarts';
import { camelize, snakify } from 'helpers/syntaxHelper';
import { COLOR_GRADIENTS, COLOR_SCALES, PaletteName } from 'styles/theme';

export const getMappedColor = (
    dimensionValue: string | undefined,
    index: number,
    currentColors: ColorMapping[] = [],
    defaultColors: string[],
    useGradientColor?: boolean,
): string | undefined => {
    if (useGradientColor) {
        return undefined;
    }
    const currentColorMapping = currentColors.find(
        (colorMapping) => colorMapping.value === dimensionValue,
    );
    return currentColorMapping
        ? currentColorMapping.color
        : defaultColors[index % defaultColors.length];
};

export const getCustomColorMappings = (
    customPalette: CustomPalette | undefined,
    palette: PaletteName | undefined,
    legendDimensionKey: string,
) => {
    if (!palette || !customPalette) {
        return [];
    }
    const customColorLegends = customPalette[palette];
    if (!customColorLegends) {
        return [];
    }
    // We use the transformed key because the dimension key is snakified and then caemlized by the API
    const transformedKey = camelize(snakify(legendDimensionKey));
    return (
        customColorLegends[legendDimensionKey] ??
        customColorLegends[transformedKey] ??
        []
    );
};

/** Convert hex color to rgb colors
 * see: https://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb
 * @param {string} color - A color in hexadecimal format (ex: #37c7cc)
 * @returns {[number, number, number]} The same color as an array of color component [r, g, b] coded from 0 to 255.
 * */
const colorValues = (color: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
    if (!result) {
        return [0, 0, 0];
    }
    return result.slice(-3).map((res) => parseInt(res, 16));
};

/**
 * Get color steps (gradient) between two colors.
 * https://gist.github.com/av01d/538b3fffc78fdc273894d173a83c563f
 * @param {string} colorStart Any color. e.g.: 'red', '#f0f', '#ff00ff', 'rgb(x,y,x)', 'rgba(r,g,b,a)', 'hsl(180, 50%, 50%)'
 * @param {string} colorEnd Any color
 * @param {int} steps Number of color steps to return
 * @returns {array} Array of 'rgb(r,g,b)' or 'rgba(r,g,b,a)' arrays
 */
const getColorSteps = (colorStart: string, colorEnd: string, steps: number) => {
    const start = colorValues(colorStart);
    const end = colorValues(colorEnd);

    let alpha = 0;

    const colors: string[] = Array.from({ length: 10 }, () => {
        alpha += 1.0 / steps;

        const c = [
            Math.round(end[0] * alpha + (1 - alpha) * start[0]),
            Math.round(end[1] * alpha + (1 - alpha) * start[1]),
            Math.round(end[2] * alpha + (1 - alpha) * start[2]),
        ];

        return `rgb(${c[0]},${c[1]},${c[2]})`;
    });

    return colors;
};

/**
 * Determine the color of the gauge:
 * - If gradient is enabled, use the gradient color and the filled ratio
 * - Else, use the second color of the palette
 * @param filledRatioRaw - The gauge ratio.
 * @param settings - The chart settings.
 * @param colors - The palette colors.
 * @returns List of color stops.
 */
export const getGaugeColorStops = (
    filledRatioRaw: number,
    settings: ChartSettingsT,
    colors: string[],
): LinearGradientObject['colorStops'] => {
    let color = colors[1];

    if (settings.useGradientColor && settings.palette) {
        const gradientColors = COLOR_GRADIENTS[settings.palette];
        const gradient = settings.reverseGradientColor
            ? gradientColors.slice().reverse()
            : gradientColors;
        const colorSteps = gradient.flatMap((gradientColor, index) => {
            if (index === 0) {
                return [];
            }
            return getColorSteps(gradient[index - 1], gradientColor, 10);
        });
        const colorStepsLength = colorSteps.length;
        const colorIndex = Math.round(filledRatioRaw * colorStepsLength);
        color = colorSteps[colorIndex] ?? colorSteps[colorStepsLength - 1];
    }
    return [
        {
            offset: 0,
            color,
        },
        {
            offset: 1,
            color,
        },
    ];
};

/**
 * Return the color scale of the palette used.
 * By default, if no palette is choosen, it will
 * return the kandinsky color scale.
 *
 * @param palette - The name of the palette to use.
 * @returns The color scale of the palette,
 * or the kadinsky color scale if the palette is undefined.
 */
export const getColorsScale = (palette: PaletteName | undefined) => {
    if (palette) return COLOR_SCALES[palette];
    return COLOR_SCALES.kandinsky;
};
